import { TileMotion } from "./tile-motion.js";

/**
 * Moving one tile of a word-order card, and writing down where they stand.
 *
 * What `./fuxi-tiles.ts` does on a press or a key, kept apart from the
 * listening. A tile moves between the row it arrives in and the line a reader
 * builds the sentence on, and the field the form posts follows every move.
 */

/** The event a tile sends as it is placed, for the session to say it. */
export const tilePlaced = "cb-fuxi:tile-placed";

/**
 * Where a tile names the recording a tap on it plays.
 *
 * Its own attribute rather than the `data-cb-fuxi-say` a listening card's cue
 * carries. A card swapped into a session says the first of those it holds, and
 * a tile wearing one would be said as the card arrived.
 * `src/user/fuxi-tiles-markup.ts` writes it.
 */
export const tileSaidAttribute = "data-cb-fuxi-tile-say";

/** The card's tiles, the line and the row together. */
export const tiles = "[data-cb-fuxi-tiles]";

/** A tile in the row, which names its place. */
export const inRow = "[data-cb-fuxi-tile]";

/** A tile on the line, which names the place in the row it came from. */
export const onLine = "[data-cb-fuxi-placed]";

/** The line the tiles are put on. */
export const line = "[data-cb-fuxi-line]";

/** The field the order is posted in. */
const field = "[data-cb-fuxi-arranged]";

/** Write the order the line holds into the field. */
function record(card: Element): void {
  const order = [...card.querySelectorAll(onLine)].map((placed) =>
    placed.getAttribute("data-cb-fuxi-placed"),
  );

  card.querySelector<HTMLInputElement>(field)!.value = order.join(" ");
}

/**
 * The next tile still in the row after this one, or Check where none is.
 *
 * Where the caret goes once a tile has been placed. The tile it was on is
 * disabled, and a disabled button gives its focus to the page, which would
 * leave a reader tabbing back through the card to carry on.
 */
function nextControl(card: Element, tile: Element): HTMLElement | null {
  const waiting = [...card.querySelectorAll<HTMLElement>(inRow)].filter(
    (one) => !one.hasAttribute("disabled"),
  );
  const after = waiting.find(
    (one) =>
      tile.compareDocumentPosition(one) & Node.DOCUMENT_POSITION_FOLLOWING,
  );

  return (
    after ??
    waiting[0] ??
    card.closest("form")!.querySelector<HTMLElement>(".cb-fuxi-check")
  );
}

/**
 * Put a tile from the row on the line, at the end unless a tile on the line
 * is named to go before.
 *
 * The copy carries the tile's characters and not its digit, the digit being
 * how a reader reaches a tile in the row and nothing on the line answering
 * to one. A tile dropped between two on the line names the one after it
 * (`./tile-drag.ts`).
 * @param card - The card's tiles.
 * @param tile - The tile in the row.
 * @param moveCaret - Whether to move the caret on after it.
 * @param before - The tile on the line to put it before, or nothing for the end.
 * @param origin - The dragged tile's visible box when it was released.
 */
export function place(
  card: Element,
  tile: HTMLElement,
  moveCaret: boolean,
  before: Element | null = null,
  origin?: DOMRect,
): void {
  const motion = new TileMotion(card);
  const placed = tile.ownerDocument.createElement("button");
  const at = tile.getAttribute("data-cb-fuxi-tile")!;

  placed.type = "button";
  placed.className = "cb-fuxi-tile cb-fuxi-tile-placed";
  placed.setAttribute("data-cb-fuxi-placed", at);

  for (const said of tile.querySelectorAll("[lang]")) {
    placed.append(said.cloneNode(true));
  }

  card.querySelector(line)!.insertBefore(placed, before);
  tile.setAttribute("disabled", "");
  motion.finish(tile, placed, origin);
  record(card);
  tile.dispatchEvent(new CustomEvent(tilePlaced, { bubbles: true }));

  if (moveCaret) {
    nextControl(card, tile)?.focus();
  }
}

/** Take a tile off the line and give its place in the row back. */
export function takeBack(
  card: Element,
  placed: Element,
  moveCaret: boolean,
  origin?: DOMRect,
): void {
  const motion = new TileMotion(card);
  const at = placed.getAttribute("data-cb-fuxi-placed")!;
  const tile = card.querySelector<HTMLElement>(`[data-cb-fuxi-tile="${at}"]`)!;

  placed.remove();
  tile.removeAttribute("disabled");
  motion.finish(placed, tile, origin);
  record(card);

  if (moveCaret) {
    tile.focus();
  }
}

/**
 * Move a tile already on the line to another place on it.
 * @param card - The card's tiles.
 * @param placed - The tile on the line.
 * @param before - The tile to put it before, or nothing for the end.
 * @param origin - The dragged tile's visible box when it was released.
 */
export function moveOnLine(
  card: Element,
  placed: Element,
  before: Element | null,
  origin?: DOMRect,
): void {
  if (before !== placed) {
    const motion = new TileMotion(card);
    card.querySelector(line)!.insertBefore(placed, before);
    motion.finish(placed, placed, origin);
    record(card);
  }
}
